export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: 'general' | 'technical' | 'features' | 'getting-started' | 'pricing' | 'billing';
  displayOrder: number;
}

export const FAQ_DATA: FAQ[] = [
  {
    id: "why-use-changelyric",
    question: "Why should I use ChangeLyric?",
    answer: "ChangeLyric is the only lyric swap platform with zero content filters. Upload any track, edit any lyrics, get back separated stems. Overdub Studio walks you through it section by section: every changed section generates takes you can listen to, and when one misses you swap strategies instead of starting over. I built this as a production tool for producers who understand copyright and handle licensing responsibly.",
    category: "general",
    displayOrder: 0
  },
  {
    id: "how-overdub-works",
    question: "How does Overdub Studio work?",
    answer: "Four steps. Upload any audio file and the tool converts it, transcribes it, and isolates the vocals while you work. Paste in your lyrics with [section tags] and lock the sections. Then generate each changed section on its own: pick a strategy like auto extension, queue a take, listen, and checkmark the keepers. When you are done, download the original stems plus comp lanes that place your kept takes on the song timeline, ready to finish in a DAW.",
    category: "features",
    displayOrder: 1
  },
  {
    id: "overdub-vs-classic",
    question: "What is the difference between Overdub Studio, Classic, and Horizon?",
    answer: "Overdub Studio is the current recommended experience and replaces the older tools for almost everything. It generates per section with ten strategies, so a failed take costs you nothing (credits are refunded) and you just try another method. Classic is the older whole-song engine and still wins on very dense rap or rapid fire vocals with 700+ words. Horizon was a manual extension tool that most people found hard to use correctly; Studio's manual extension mode covers that use case with guardrails.",
    category: "features",
    displayOrder: 2
  },
  {
    id: "section-tags",
    question: "Why do my section tags matter so much?",
    answer: "Section tags are how the engine understands which parts of the song share a melody and a vibe. If two sections have the same melodic idea, label them the same way (for example, both choruses should be labeled chorus) so the engine treats them as the same musical idea. If a section is mislabeled, fix it before you lock the sections. The original transcription is a best guess and is often worth correcting.",
    category: "features",
    displayOrder: 3
  },
  {
    id: "split-long-sections",
    question: "What if my verse is really long?",
    answer: "If a section runs close to a minute or longer, split it in half. Use tags like verse 1.1 and verse 1.2 on both the original and new lyric columns. The tags and line counts between the two columns have to match exactly or generation stays disabled. Splitting long sections helps the engine handle them cleanly, and a shorter block also lowers the chance of a failed take. Most songs do not need this, but if a verse feels too long, split it.",
    category: "features",
    displayOrder: 4
  },
  {
    id: "preview-is-a-scratch-track",
    question: "Why does the take audio sound a little off?",
    answer: "Each take you audition is a raw generation straight from the engine, with the instrumental still underneath. That is on purpose: you are judging the vocal performance, not a finished mix. After you mark a keeper, the tool isolates the vocal on that take automatically. The final assembly happens in the comp lane export and your DAW, where you get something significantly cleaner than the audition.",
    category: "features",
    displayOrder: 5
  },
  {
    id: "acapella-gets-full-mix",
    question: "I uploaded an acapella. Why do takes come back with instrumental underneath?",
    answer: "Generation runs on the full master mix on purpose, because the engine performs better and runs faster on full mixes than on isolated vocals. Every audition take therefore includes the instrumental, even from an acapella source. Mark the take you want as Keep and Studio isolates the vocal automatically, so playback and the download give you vocals only.",
    category: "features",
    displayOrder: 6
  },
  {
    id: "whats-in-the-download",
    question: "What is in the Overdub Studio download?",
    answer: "Two downloads. The original stems ZIP has the master, the vocal bus, the lead vocal, the backing vocal, and the instrumental. The comp lanes download takes every take you checkmarked, isolates it to just the vocal, and places it at its original spot on the song timeline, so the lanes drop straight into your DAW and you cut the final vocal together in minutes instead of hours.",
    category: "features",
    displayOrder: 7
  },
  {
    id: "file-formats",
    question: "What file formats are supported?",
    answer: "Overdub Studio accepts MP3, WAV, M4A, FLAC, and OGG files up to 100MB, and converts them automatically. You get back separated stems that can be imported into any DAW.",
    category: "technical",
    displayOrder: 8
  },
  {
    id: "daw-compatibility",
    question: "Is this compatible with all DAWs?",
    answer: "Yes. The output stems work with Pro Tools, Logic Pro, Ableton Live, FL Studio, Cubase, Studio One, and any other DAW that imports audio files.",
    category: "technical",
    displayOrder: 9
  },
  {
    id: "different-lyrics",
    question: "Can I change lyrics to say something completely different?",
    answer: "Yes. There are no content restrictions or censorship filters. Line counts between the original and new lyrics have to match line for line, but the words can be whatever you want. Smaller changes per pass tend to produce more natural results and fail less, but full rewrites are fully supported.",
    category: "features",
    displayOrder: 10
  },
  {
    id: "ease-of-use",
    question: "How easy is this to use?",
    answer: "Overdub Studio is the most guided the tool has ever been: a four-step workspace that walks you from upload to stems, with auto extension as a sensible default so you do not have to master every strategy on day one. This is still a professional tool though. You need DAW experience to comp and mix the output stems into a finished track. If you use a DAW daily, you will pick it up fast.",
    category: "getting-started",
    displayOrder: 11
  },
  {
    id: "overdub-speed",
    question: "How fast is Overdub Studio processing?",
    answer: "Transcription, section timing, and vocal isolation all run in the background while you write your lyrics, so the song is ready by the time you are. Each generation queues a take that typically comes back in a few minutes, and you can queue several sections at once and keep working while they run.",
    category: "features",
    displayOrder: 12
  },
  {
    id: "limitations",
    question: "When should I NOT use Overdub Studio?",
    answer: "Three cases. First, spoken audio (use the Speech Changer instead, which is built for that). Second, very dense rap or rapid fire vocals with 700+ words, where the Classic engine is usually a better fit. Third, opera and musicals: the structure fights the engine, and the workaround is having someone sing a pilot vocal, then training a voice model on an isolated lead vocal and converting it with the Voice Changer. Standard pop, country, and verse chorus structures are where Studio performs best.",
    category: "features",
    displayOrder: 13
  },
  {
    id: "translation",
    question: "Is this good for translation?",
    answer: "Usually not. Overdub is built to change lyrics in the same language as the original. Translating a song properly requires hiring a singer to create a new pilot vocal and then doing AI processing to make it sound like the original. It is genuinely hard to do well. Hire our team if you need help with select languages.",
    category: "technical",
    displayOrder: 14
  },
  {
    id: "content-filters",
    question: "Are there content filters or copyright detection?",
    answer: "No. ChangeLyric has zero content moderation filters. No audio fingerprinting, no automated rejections, no false positives blocking your own productions. You decide what you have rights to modify. Users are responsible for understanding applicable copyright laws.",
    category: "features",
    displayOrder: 15
  },
  {
    id: "credits-explained",
    question: "How do credits work?",
    answer: "Credits are shared across the ChangeLyric tools. In Overdub Studio, each generation you queue costs 1 credit, and failed takes are refunded automatically, so a miss never costs you. Training an upscale voice model from your project's lead vocal costs 30 credits. Your credit balance resets each billing cycle.",
    category: "pricing",
    displayOrder: 16
  },
  {
    id: "rollover",
    question: "Do unused credits roll over?",
    answer: "No. Credits reset at the beginning of each billing cycle and do not carry over.",
    category: "pricing",
    displayOrder: 17
  },
  {
    id: "cancel-anytime",
    question: "Can I cancel anytime?",
    answer: "Yes. Cancel from your account dashboard at any time. You keep access through the end of your current billing period.",
    category: "billing",
    displayOrder: 18
  },
  {
    id: "improve-results",
    question: "How can I improve my results?",
    answer: "After downloading your stems, open them in a DAW and line up the raw takes by hand where the auto-comp missed. Use side chain compression on the original vocal bus, keyed by your new lead and backing tracks, to duck the original out when the new vocal sits. Run the comped vocal through the Voice Changer to smooth tonal differences. Pre-processing complex mixes with the Vocal Splitter before upload can also help. As a last polish, running the result through Suno's Cover feature can smooth lingering issues.",
    category: "general",
    displayOrder: 19
  },
  {
    id: "quality",
    question: "How good is the audio quality?",
    answer: "No AI vocal tool delivers perfect studio results yet. Current testing puts the fail rate per pass at about 50%, and the Studio is designed around that instead of hiding it: you see exactly which take ignored your change, and you re-run with a different strategy, scramble setting, or tag grouping until you get what you need. When a take sounds thin or demo quality, train a voice model from the project's lead vocal and upscale it with the Voice Changer.",
    category: "technical",
    displayOrder: 20
  },
  {
    id: "vocal-splitting",
    question: "Do you offer vocal separation tools?",
    answer: "Yes. The Vocal Isolator separates vocals from instrumentals. The Vocal Splitter separates lead vocals from backing vocals. Both are included in your subscription and accessible from the dashboard.",
    category: "technical",
    displayOrder: 21
  },
  {
    id: "free-trial",
    question: "Is there a free trial?",
    answer: "Yes. New users get a 7-day free trial with enough credits to actually test Overdub on a real project. The trial requires a card on file so you have time to use the tool, see if it works for your song, and cancel before the trial ends if it is not the right fit. No charge until day 7.",
    category: "pricing",
    displayOrder: 22
  },
  {
    id: "zip",
    question: "How do I turn the download into a finished song?",
    answer: "You get the original stems plus comp lanes that place your kept takes on the song timeline, isolated to the vocal. Finishing the song still requires editing in a DAW. If you do not want to do that yourself, our team has done over 600 orders and can handle it for you. Submit your lyrics through the service page and we will produce the audio.",
    category: "technical",
    displayOrder: 23
  },
  {
    id: "trial-upgrade",
    question: "I am on a free trial. How do I upgrade?",
    answer: "If you are on a free trial, you are already on the paid plan for the trial window. There is no Upgrade button because there is nothing to upgrade to. Your card is billed automatically on day 7, and you can cancel before then from Billing in your profile menu if it is not the right fit. If a checkout page says payment is due now when you try to upgrade, close it. That message appears when the system thinks you are starting a second subscription on top of the one already active. You do not owe anything during the trial. If it keeps happening, email contact@changelyric.com and support will fix it.",
    category: "billing",
    displayOrder: 24
  }
];
